import type { WatchStopHandle } from "vue"
import { useWebSocket, type UseWebSocketReturn } from "@vueuse/core"
import { Subject } from "rxjs"

export const useAiSocket = defineComposable("ai-socket", () => {
	const config = useRuntimeConfig()
	const $message = new Subject<Record<string, any>>()
	const { logout } = useLogin()

	let ws: UseWebSocketReturn<any> | null = null

	const isConnected = ref(false)
	let watchHandler: WatchStopHandle | null = null

	const subscription = onMessage()
		.subscribe(async (event) => {
			if (event.type === "Unathenticated") {
				subscription.unsubscribe()
				await logout()
			}
		})

	// Might be called multiple times
	function createSocket(cookie: string) {
		close()

		if (watchHandler)
			watchHandler()

		ws = useWebSocket(config.public.server.ws, {
			immediate: false,
			autoReconnect: {
				delay: 1000,
			},
			protocols: [cookie],
			async onMessage(_event, message) {
				const textRes: string = await message.data
				$message.next(JSON.parse(textRes))
			},
		})

		watchHandler = watch(ws.status, () => {
			isConnected.value = ws!.status.value === "OPEN"
		}, { immediate: true })
	}

	function onMessage() {
		return $message.asObservable()
	}

	function open() {
		if (ws)
			ws.open()
	}

	function close() {
		if (ws)
			ws.close()
	}

	function send(data: string | ArrayBuffer | Blob, useBuffer?: boolean) {
		if (ws)
			ws.send(data, useBuffer)
	}

	return {
		createSocket,
		send,
		open,
		close,
		onMessage,
		// Avoid `readonly change` warning
		isConnected: computed(() => isConnected.value),
	}
})
